import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, GraduationCap, BookOpen, Briefcase, Building2, Euro, Shield, ChevronRight, MapPin, Clock } from 'lucide-react';
import SmartProfileEvalCTA from './components/SmartProfileEvalCTA';
import { usePageMeta } from '@/hooks/useSEO';

const quickLinks = [
  { label: 'Ireland Student Visa', path: '/study-abroad/ireland/student-visa/', icon: Shield },
  { label: 'Cost of Study', path: '/study-abroad/ireland/cost-of-study/', icon: Euro },
  { label: 'Jobs After Study', path: '/study-abroad/ireland/jobs-after-study/', icon: Briefcase },
];

const whyIreland = [
  { title: '2-Year Stay-Back Visa', desc: 'The Third Level Graduate Programme allows graduates to stay and work in Ireland for up to 2 years after completing their degree.' },
  { title: 'Europe\'s Tech Capital', desc: 'Dublin is the European headquarters for Google, Facebook, Apple, LinkedIn, and hundreds of tech companies.' },
  { title: 'English-Speaking EU Nation', desc: 'No language barrier — study in English while having access to the entire EU job market.' },
  { title: 'High Graduate Employment', desc: 'Ireland has one of the highest graduate employment rates in Europe at over 90% within 9 months.' },
  { title: 'Affordable vs UK', desc: 'Tuition and living costs are generally 20-30% lower than the UK, making it a cost-effective alternative.' },
  { title: 'Pathway to EU Residency', desc: 'After 5 years of legal residence, you can apply for permanent residency in Ireland — with access to all EU countries.' },
];

const topPrograms = [
  { name: 'Computer Science & IT', demand: 'Very High', avgSalary: '\u20AC45,000' },
  { name: 'Data Analytics & AI', demand: 'Very High', avgSalary: '\u20AC48,000' },
  { name: 'Pharmaceutical Sciences', demand: 'High', avgSalary: '\u20AC42,000' },
  { name: 'Business & Finance', demand: 'High', avgSalary: '\u20AC40,000' },
  { name: 'Nursing & Healthcare', demand: 'Very High', avgSalary: '\u20AC35,000' },
  { name: 'Engineering', demand: 'High', avgSalary: '\u20AC43,000' },
];

const costParameters = [
  { item: 'Tuition (Masters)', range: '\u20AC12,000 - \u20AC25,000/year' },
  { item: 'Living Expenses', range: '\u20AC8,000 - \u20AC12,000/year' },
  { item: 'Student Visa Fee', range: '\u20AC60' },
  { item: 'IRP Registration', range: '\u20AC300' },
  { item: 'Health Insurance', range: '\u20AC500 - \u20AC700/year' },
  { item: 'Proof of Funds', range: '\u20AC10,000 (bank statement)' },
];

const topCities = [
  { name: 'Dublin', highlight: 'Tech & Business Hub', desc: 'Home to Silicon Docks — European HQ of major tech companies' },
  { name: 'Cork', highlight: 'Pharma & Life Sciences', desc: 'Ireland\'s second city with a strong pharmaceutical industry' },
  { name: 'Galway', highlight: 'Culture & MedTech', desc: 'Vibrant university city with a booming medtech sector' },
  { name: 'Limerick', highlight: 'Affordable & Growing', desc: 'Lower cost of living with strong engineering and IT sectors' },
];

const IrelandHubPage = () => {
  usePageMeta(
    'Study in Ireland 2026: Universities, Visa & 2-Year Stay-Back | Zafco',
    'Complete guide to studying in Ireland in 2026: top universities, Stamp 2 student visa, €10,000 living-cost rule, scholarships, 2-year Third Level Graduate stay-back visa & PR pathways.',
  );
  return (
    <div data-testid="sa-ireland-hub">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #16A34A 0%, #003087 60%, #001F5C 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-80 h-80 bg-[#FF8C00] rounded-full blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
              <span className="text-lg">{getFlagEmoji('IE')}</span>
              <span className="text-xs text-white/90 font-medium">Study Destination</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-syne text-white leading-tight mb-5">
              Study in <span className="text-[#4ADE80]">Ireland</span>
            </h1>
            <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl">
              Ireland is Europe's tech capital, offering English-taught programs, a 2-year stay-back visa,
              and direct access to the EU job market. Home to Google, Apple, Facebook's European headquarters.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/study-abroad/book-counselling/"
                className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-8 py-3.5 rounded-full transition-all sa-pulse-cta"
              >
                Get Free Ireland Counselling <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/study-abroad/free-profile-evaluation/"
                className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-sm px-8 py-3.5 rounded-full transition-all"
              >
                Check Eligibility
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="bg-[#001F5C] py-8">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { val: '35,000+', label: 'International Students' },
            { val: '2 Years', label: 'Stay-Back Visa' },
            { val: '90%+', label: 'Graduate Employment' },
            { val: 'Top 1%', label: 'Universities Globally' },
          ].map((s) => (
            <div key={s.label} className="sa-stat-card rounded-xl p-4 text-center">
              <div className="text-2xl font-bold font-syne text-[#C5A028]">{s.val}</div>
              <div className="text-xs text-slate-400 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="sa-gold-line mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#003087] mb-3">
              Study in Ireland — Everything You Need
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {quickLinks.map((link) => (
              <Link key={link.path} to={link.path} className="sa-feature-card bg-white rounded-xl p-5 flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-lg bg-[#003087]/10 flex items-center justify-center shrink-0">
                  <link.icon className="w-5 h-5 text-[#003087]" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-900 group-hover:text-[#003087] transition-colors">{link.label}</h3>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-[#003087] mt-1 transition-colors" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stay-Back Rules */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="sa-gold-line mb-4" />
              <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#003087] mb-5">
                Stay-Back & Work Rights
              </h2>
              <div className="space-y-4">
                {[
                  { icon: Clock, text: '2-year stay-back under Third Level Graduate Programme (Level 9/10 NFQ)' },
                  { icon: Clock, text: '1-year stay-back for Honours Bachelor\'s degree (Level 8 NFQ)' },
                  { icon: Briefcase, text: 'Work 20 hrs/week during term, 40 hrs/week during holidays' },
                  { icon: Shield, text: 'Critical Skills Employment Permit for in-demand occupations' },
                  { icon: CheckCircle, text: 'After 5 years of legal residence, apply for permanent residency' },
                  { icon: Building2, text: 'Stamp 1G visa allows unrestricted employment search' },
                ].map((item) => (
                  <div key={item.text} className="flex items-start gap-3">
                    <item.icon className="w-5 h-5 text-[#C5A028] shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <h3 className="text-base font-bold font-syne text-[#003087] mb-4">Cost Overview</h3>
              <div className="space-y-3">
                {costParameters.map((cp) => (
                  <div key={cp.item} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-700">{cp.item}</span>
                    <span className="text-sm font-semibold text-slate-900">{cp.range}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/study-abroad/cost-calculator/"
                className="inline-flex items-center gap-2 mt-4 text-[#003087] font-semibold text-xs hover:gap-3 transition-all"
              >
                Use Cost Calculator <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Ireland */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="sa-gold-line mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#003087] mb-3">Why Study in Ireland?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyIreland.map((item) => (
              <div key={item.title} className="bg-white rounded-xl p-6 border border-slate-100">
                <h3 className="text-base font-bold font-syne text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Programs */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="sa-gold-line mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#003087] mb-3">Top Programs in Ireland</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {topPrograms.map((prog) => (
              <div key={prog.name} className="bg-white rounded-xl p-5 border border-slate-100">
                <h3 className="text-sm font-bold text-slate-900 mb-2">{prog.name}</h3>
                <div className="flex items-center justify-between text-xs">
                  <span className={`px-2 py-0.5 rounded-full font-medium ${
                    prog.demand === 'Very High' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                  }`}>{prog.demand} Demand</span>
                  <span className="text-slate-500">Avg: {prog.avgSalary}/yr</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Cities */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="sa-gold-line mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold font-syne text-[#003087] mb-3">Top Study Cities</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {topCities.map((city) => (
              <div key={city.name} className="sa-feature-card bg-white rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <MapPin className="w-4 h-4 text-[#003087]" />
                  <h3 className="text-sm font-bold text-slate-900">{city.name}</h3>
                  <span className="text-xs text-[#C5A028] font-medium">{city.highlight}</span>
                </div>
                <p className="text-xs text-slate-500">{city.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Smart Profile-Eval CTA (cross-link to existing free-profile-evaluation tool) */}
      <SmartProfileEvalCTA country="Ireland" country_slug="ireland-hub" testId="ireland-hub-smart-cta" />

      {/* CTA */}
      <section className="sa-hero-gradient py-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold font-syne text-white mb-4">Start Your Ireland Study Journey</h2>
          <p className="text-base text-slate-300 mb-8 max-w-2xl mx-auto">
            Get personalized guidance on university selection, student visa process, and stay-back options for Ireland.
          </p>
          <Link
            to="/study-abroad/book-counselling/"
            className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-8 py-3.5 rounded-full transition-all"
          >
            Book Free Ireland Counselling <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

function getFlagEmoji(countryCode) {
  const codePoints = countryCode.toUpperCase().split('').map((c) => 127397 + c.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

export default IrelandHubPage;
